import React from "react";
import { ICompany } from "../../../customTypings/company/Company";
import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import {
  EditFormActions,
  EditFormSaveCancelButtons,
  FormAlert,
} from "../../Forms";
import {
  companyValidationSchema,
  createEditFormSubmitHandler,
  emptyStringToNull,
  hydratePostalAddressForForm,
  mapPostalAddressToApi,
  normalizePhoneInput,
  nullToEmptyString,
} from "../../../utils";
import { useFormSubmit } from "../../../hooks";
import { companyService } from "../../../services";
import { CompanyInfoFormGrid } from "./CompanyInfoFormGrid";

const emptyCompany: ICompany = {
  id: "",
  phone: "",
  contactEmail: "",
  addressLine1: "",
  addressLine2: "",
  addressLine3: "",
  town: "",
  county: "",
  country: "United Kingdom",
  postcode: "",
  website: "",
  invitationEmail: "",
};

function normalizeCompanyForForm(company?: ICompany): ICompany {
  if (!company) return { ...emptyCompany };
  return {
    ...emptyCompany,
    ...company,
    ...hydratePostalAddressForForm(company),
    phone: nullToEmptyString(company.phone),
    contactEmail: nullToEmptyString(company.contactEmail),
    website: nullToEmptyString(company.website),
    invitationEmail: nullToEmptyString(company.invitationEmail),
  };
}

function normalizeCompanyPayload(values: ICompany): ICompany {
  const address = mapPostalAddressToApi(values);
  return {
    ...values,
    phone: normalizePhoneInput(values.phone),
    contactEmail: nullToEmptyString(values.contactEmail).trim(),
    invitationEmail: nullToEmptyString(values.invitationEmail).trim(),
    website: emptyStringToNull(values.website),
    addressLine1: address.addressLine1,
    addressLine2: address.addressLine2,
    addressLine3: address.addressLine3,
    town: address.town,
    county: address.county,
    postcode: address.postcode,
    country: address.country,
  };
}

interface CompanyInfoFormProps {
  company?: ICompany;
  onSuccess?: () => void;
}

export const CompanyInfoForm: React.FC<CompanyInfoFormProps> = ({ company, onSuccess }) => {
  const navigate = useNavigate();
  const { error, submitForm } = useFormSubmit({
    onSubmit: async (values: ICompany) => {
      const serviceMethod = company?.id ? companyService.update : companyService.create;
      return await serviceMethod(normalizeCompanyPayload(values));
    },
    onSuccess: () => {
      onSuccess?.();
    },
    successMessage: company?.id
      ? "Company information updated successfully"
      : "Company information created successfully",
  });

  const initialValues = normalizeCompanyForForm(company);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={companyValidationSchema}
      onSubmit={submitForm}
      enableReinitialize
    >
      {({ isSubmitting, handleSubmit, validateForm, setTouched, values }) => (
        <Form
          noValidate
          onSubmit={createEditFormSubmitHandler(validateForm, setTouched, values, handleSubmit)}
        >
          <FormAlert message={error || undefined} variant="danger" />

          <CompanyInfoFormGrid />

          <EditFormActions>
            <EditFormSaveCancelButtons
              isSubmitting={isSubmitting}
              onCancel={() => navigate("/settings")}
            />
          </EditFormActions>
        </Form>
      )}
    </Formik>
  );
};
